import { memo, useEffect, useState } from "react";
import { TextField } from "@mui/material";
import { Button, Dropdown } from "semantic-ui-react";
import ArrayEntry from "./ArrayEntry";
import StringEntry from "./StringEntry";
import UCUM from "../dictionaries/UCUM";
import UriEntry, { CreateHyperlink } from "./UriEntry";
import { displayStringFromFHIRCoding } from "./CodingEntry";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";

const CodingDisplay = ({ coding }) => {
    if (coding) {
        if (Array.isArray(coding) || typeof coding !== "object") {
            return <>[ERROR: Coding data not interpretable.]</>;
        }
        let code = coding.code || "[Code missing.]";
        let system = coding.system || "[Code System missing.]";
        let systemDisplay;
        if (system === "http://snomed.info/sct") {
            systemDisplay = "SNOMED-CT";
        } else if (system === "http://loinc.org") {
            systemDisplay = "LOINC";
        } else if (system === "http://www.nlm.nih.gov/research/umls/rxnorm") {
            systemDisplay = "RxNorm";
        } else if (system === "http://unitsofmeasure.org") {
            systemDisplay = "UCUM";
        }
        if (coding.display && systemDisplay) {
            return <>{coding.display} (<CreateHyperlink url={system} display={systemDisplay || system} openInNewTab={true} /> code: {code})</>
        } else if (coding.display && !systemDisplay) {
            return <>{coding.display} (coded as: {code} from <CreateHyperlink url={system} display={systemDisplay || system} openInNewTab={true} />)</>
        } else if (systemDisplay) {
            return <><CreateHyperlink url={system} display={systemDisplay || system} openInNewTab={true} /> code: {code}</>
        } else {
            return <>code {code} from <CreateHyperlink url={system} display={systemDisplay || system} openInNewTab={true} /></>
        }
    } else {
        return <></>
    }
}

const DisplayFromFHIRCodeableConcept = ({ codeableConcept }) => {
    if (codeableConcept) {
        if (typeof codeableConcept === "string") {
            return <>{codeableConcept} [ERROR: CodeableConcept data not in expected CodeableConcept datatype.]</>
        }
        if (Array.isArray(codeableConcept) || typeof codeableConcept !== "object") {
            return <>[ERROR: CodeableConcept data not interpretable.]</>;
        }
        if (!codeableConcept.text && !codeableConcept.coding) {
            return <>[ERROR: CodeableConcept data not interpretable.]</>;
        }
        try {
            return <>{codeableConcept.coding && codeableConcept.coding.length === 1 &&
                <CodingDisplay coding={codeableConcept.coding[0]} />}
                {codeableConcept.coding && codeableConcept.coding.length > 1 && <>
                    {codeableConcept.coding.map((coding, codingIndex) => {
                        return <span key={codingIndex}>
                            <CodingDisplay coding={coding} />
                            {codingIndex !== codeableConcept.coding.length - 1 && <>; </>}
                        </span>
                    })}
                </>}
                {(codeableConcept.text && codeableConcept.coding) &&
                    <>; {codeableConcept.text}</>}
                {(codeableConcept.text && !codeableConcept.coding) &&
                    <>{codeableConcept.text}</>}
                {(codeableConcept.extension?.[0]?.url === "http://hl7.org/fhir/StructureDefinition/codeOptions" &&
                    codeableConcept.extension[0].valueCanonical) &&
                    <>; code options defined at <CreateHyperlink url={codeableConcept.extension[0].valueCanonical}
                        display={codeableConcept.extension[0].valueCanonical} openInNewTab={true} /></>}
            </>
        } catch {
            return <>[ERROR: CodeableConcept data not interpretable.]</>;
        }
    } else {
        return <></>
    }
}

const simpleDisplayStringFromFHIRCodeableConcept = (concept) => {
    let display = "";
    display = concept?.coding?.[0]?.display;
    if (concept?.text) {
        if (display) {
            display += ", " + concept.text;
        } else {
            display = concept.text;
        }
    }
    if (concept?.extension?.[0]?.url === "http://hl7.org/fhir/StructureDefinition/codeOptions" && concept.extension[0].valueCanonical) {
        if (display) {
            display += ", code options defined at " + concept.extension[0].valueCanonical;
        } else {
            display = "code options defined at " + concept.extension[0].valueCanonical;
        }
    }
    return display;
};

const displayStringFromFHIRCodeableConcept = (codeableConcept) => {
    if (codeableConcept === undefined || codeableConcept === null || codeableConcept === '') {
        return ""
    }
    if (typeof codeableConcept === "object") {
        try {
            let codeableConceptDisplay = "";
            if (codeableConcept.coding) {
                for (let codingIndex in codeableConcept.coding) {
                    let coding = codeableConcept.coding[codingIndex];
                    if (coding.display || coding.code) {
                        if (codeableConceptDisplay !== "") {
                            codeableConceptDisplay += "; ";
                        }
                        codeableConceptDisplay += displayStringFromFHIRCoding(coding);
                    }
                }
            }
            if (codeableConcept.text) {
                if (codeableConceptDisplay !== "") {
                    codeableConceptDisplay += "; ";
                }
                codeableConceptDisplay += codeableConcept.text;
            }

            if (Array.isArray(codeableConcept.extension)) {
                for (let extension of codeableConcept.extension) {
                    if (extension.url === "http://hl7.org/fhir/StructureDefinition/codeOptions" && extension.valueCanonical) {
                        if (codeableConceptDisplay !== "") {
                            codeableConceptDisplay += "; ";
                        }
                        codeableConceptDisplay += "code options defined at " + extension.valueCanonical;
                    }
                }
            }
            if (codeableConceptDisplay !== "") {
                return codeableConceptDisplay;
            }
        } catch (e) { console.log(e); }
    }
    if (typeof codeableConcept === "string") {
        return `${codeableConcept} [ERROR: CodeableConcept data not in expected CodeableConcept datatype.]`
    }
    return "[ERROR: CodeableConcept data not interpretable.]";
}

const ChooseItemForCodeableConceptEntry = memo(({ elementName, fieldLabel, startingValue,
    setResourceState, codeableConceptValueSet, setOpenCodeableConceptEntryState }) => {

    let startingCode = "";
    if (startingValue?.coding?.length > 0 && startingValue.coding[0].code) {
        startingCode = startingValue.coding[0].code
    } else {
        startingCode = startingValue?.text || "";
    }

    if (!codeableConceptValueSet) {
        return <></>
    }
    let fullValueSet = [{
        "text": "Enter a value not in this list."
    }].concat(codeableConceptValueSet);

    if (startingCode) {
        let matchFound = false;
        for (const item of fullValueSet) {
            if (item.coding?.length > 0 && item.coding[0].code === startingCode) {
                matchFound = true;
            } else if (item.text === startingCode) {
                matchFound = true;
            }
        }
        if (!matchFound) {
            fullValueSet.push({ "text": startingCode });
        }
    }

    return <>
        <p style={{ marginBottom: "2px" }}><b>Pick {fieldLabel}: </b></p>
        <div style={{ marginLeft: "24px" }}>
            <Dropdown
                name={"picklist-for-" + elementName}
                placeholder="Preset" search
                closeOnChange selection clearable selectOnBlur={false} autoComplete="off"
                style={{ minWidth: "25%" }}
                options={fullValueSet.map((term, termIndex) => {
                    let termCode = "";
                    let termDisplay = "";
                    if (term.coding?.length > 0) {
                        termCode = term.coding[0].code || "";
                        termDisplay = term.coding[0].display || "";
                    }
                    if (term.text) {
                        if (!termCode) {
                            termCode = term.text;
                        }
                        if (termDisplay) {
                            termDisplay += "; " + term.text;
                        } else {
                            termDisplay = term.text;
                        }
                    }
                    return { "key": termIndex, "value": termCode, "text": termDisplay }
                })}
                value={startingCode}
                onChange={(e, data) => {
                    let selectedText;
                    let selectedCoding;
                    for (const term of fullValueSet) {
                        if (term.text === data.value || (term.coding?.length > 0 && term.coding[0].code === data.value)) {
                            selectedCoding = term.coding || "";
                            selectedText = term.text || "";
                        }
                    }
                    let newCodeableConcept = { coding: selectedCoding, text: selectedText };
                    if (selectedCoding || selectedText) {
                        if (selectedCoding === "") {
                            delete newCodeableConcept.coding;
                        }
                        if (selectedText === "") {
                            delete newCodeableConcept.text;
                        }
                        if (selectedText === "Enter a value not in this list.") {
                            setOpenCodeableConceptEntryState(true);
                        } else {
                            setResourceState(newCodeableConcept);
                            setOpenCodeableConceptEntryState(false);
                        }
                    } else {
                        setResourceState({ text: "" });
                    }
                }}
            />
            &nbsp;&nbsp;&nbsp;
            <Button className="formButton" style={{ color: "#000000", marginTop: "12px", marginLeft: "4px" }}
                content="Enter a value not in this list" onClick={() => {
                    setOpenCodeableConceptEntryState(true);
                }} />
        </div>
    </>
});

const CodeableConceptEntry = memo(({ elementName, fieldLabel, startingValue, setResourceState, allowedValues,
    valueSet, dataEntryStyle, inTableCell, systemChoices, systemChoicesOpen, startCollapsed, codeableConceptLevelValueSet }) => {

    if (!startingValue && dataEntryStyle === "simple") {
        startCollapsed = true;
    }
    if (dataEntryStyle === "Age") {
        valueSet = UCUM.age;
    } else if (dataEntryStyle === "Duration") {
        valueSet = UCUM.duration;
    } else if (valueSet === "UCUM") {
        valueSet = UCUM.common;
    }

    let startingCodeableConcept = { "extension": [], "coding": [], "text": "" };
    let startingValueCanonical = "";
    if (startingValue) {
        if (startingValue.extension) {
            startingCodeableConcept.extension = startingValue.extension;
            for (const extension of startingValue.extension) {
                if (extension.url === "http://hl7.org/fhir/StructureDefinition/codeOptions") {
                    startingValueCanonical = extension.valueCanonical;
                }
            }
        }
        if (startingValue.coding) {
            startingCodeableConcept.coding = startingValue.coding;
        }
        if (startingValue.text) {
            startingCodeableConcept.text = startingValue.text;
        }
    }

    const [codeableConceptState, setCodeableConceptState] = useState(JSON.parse(JSON.stringify(startingCodeableConcept || {})));
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);
    const [openCodeableConceptEntryState, setOpenCodeableConceptEntryState] = useState(false);
    const [codeOptionsExtensionState, setCodeOptionsExtensionState] = useState({ "valueCanonical": startingValueCanonical || "" });

    useEffect(() => {
        if (codeableConceptState.extension) {
            let oldExtension = JSON.parse(JSON.stringify(codeableConceptState.extension));
            let newExtension = oldExtension.filter(ext => ext.url !== "http://hl7.org/fhir/StructureDefinition/codeOptions");
            if (codeOptionsExtensionState.valueCanonical) {
                let codeOptionsExtension = {
                    "url": "http://hl7.org/fhir/StructureDefinition/codeOptions",
                    "valueCanonical": codeOptionsExtensionState.valueCanonical
                };
                newExtension.push(codeOptionsExtension);
            }
            setCodeableConceptState(prevState => {
                return { ...prevState, "extension": newExtension };
            });
        }
    }, [codeOptionsExtensionState.valueCanonical]);

    useEffect((() => {
        if (Object.keys(codeableConceptState).length &&
            JSON.stringify(startingCodeableConcept) !== JSON.stringify(codeableConceptState)) {
            let newCodeableConcept = {};
            if (codeableConceptState.extension) {
                newCodeableConcept.extension = codeableConceptState.extension;
            }
            if (codeableConceptState.coding !== null && codeableConceptState.coding !== undefined &&
                Array.isArray(codeableConceptState.coding) && codeableConceptState.coding.length !== 0 &&
                !(codeableConceptState.coding.length === 1 && Object.keys(codeableConceptState.coding[0]).length === 0)) {
                newCodeableConcept.coding = codeableConceptState.coding;
            }
            if (codeableConceptState.text) {
                newCodeableConcept.text = codeableConceptState.text;
            }
            if (Object.keys(newCodeableConcept).length === 0) {
                newCodeableConcept = null;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newCodeableConcept } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [codeableConceptState]);

    if (dataEntryStyle === "simple") {
        return <>
            <span>
                <b>{fieldLabel}: </b>
                &nbsp;&nbsp;&nbsp;
                <TextField style={{ width: "50%", marginTop: "12px" }} multiline
                    className="inputField" type='text' label={fieldLabel} size="small" variant='outlined'
                    value={codeableConceptState.text || ""}
                    onChange={(e) => {
                        setCodeableConceptState(prevState => { return { ...prevState, 'text': e.target.value } });
                    }} />
                &nbsp;&nbsp;&nbsp;
                {startCollapsedState &&
                    <Button className="formButton" style={{ color: "#000000", marginTop: "12px", marginLeft: "4px" }}
                        content="Provide a Coding" onClick={() => {
                            setStartCollapsedState(false);
                            if (!(codeableConceptState?.coding?.length > 0)) {
                                setCodeableConceptState(prevState => { return { ...prevState, "coding": [{}] } });
                            }
                        }} />}
            </span>
            {!startCollapsedState &&
                <div>
                    <br />
                    <ArrayEntry datatype='Coding' elementName='coding' fieldLabel='Coding'
                        startingValue={Array.isArray(codeableConceptState.coding) ? codeableConceptState.coding : undefined}
                        setResourceState={setCodeableConceptState}
                        allowedValues={allowedValues} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} valueSet={valueSet}
                        systemChoices={systemChoices} systemChoicesOpen={systemChoicesOpen} />
                    <UriEntry elementName='valueCanonical' setResourceState={setCodeOptionsExtensionState}
                        fieldLabel={'Add ValueSet URL for code options'}
                        startingValue={codeOptionsExtensionState.valueCanonical} />
                </div>}
        </>
    }
    if (startCollapsedState) {
        return <>
            <div>
                <b>{fieldLabel}: </b>
                {startingValue && <DisplayFromFHIRCodeableConcept codeableConcept={startingCodeableConcept} />}
                &nbsp;&nbsp;
                <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
            </div>
        </>
    } else {
        if (codeableConceptLevelValueSet) {
            return <>
                <ChooseItemForCodeableConceptEntry elementName={elementName}
                    startingValue={startingValue || null}
                    fieldLabel={fieldLabel}
                    codeableConceptValueSet={codeableConceptLevelValueSet}
                    setResourceState={setCodeableConceptState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
                    setOpenCodeableConceptEntryState={setOpenCodeableConceptEntryState} />
                {openCodeableConceptEntryState && <>
                    <div style={{ marginLeft: "24px" }}>
                        <span><b>Enter new value: </b>&nbsp;&nbsp;&nbsp;</span>
                        <div style={{ marginLeft: "24px" }}>
                            <TextField style={{ width: "90%", marginTop: "12px" }} multiline
                                className="inputField" type='text' label={"Text for " + fieldLabel} size="small" variant='outlined'
                                value={codeableConceptState.text || ""}
                                onChange={(e) => { setCodeableConceptState(prevState => { return { ...prevState, 'text': e.target.value } }) }} />
                            &nbsp;&nbsp;&nbsp;
                            <div>
                                <br />
                                <ArrayEntry datatype='Coding' elementName='coding' fieldLabel='Coding'
                                    startingValue={Array.isArray(codeableConceptState.coding) ? codeableConceptState.coding : undefined}
                                    setResourceState={setCodeableConceptState}
                                    allowedValues={allowedValues} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} valueSet={valueSet}
                                    systemChoices={systemChoices} systemChoicesOpen={systemChoicesOpen} />
                                <UriEntry elementName='valueCanonical' setResourceState={setCodeOptionsExtensionState}
                                    fieldLabel={'Add ValueSet URL for code options'}
                                    startingValue={codeOptionsExtensionState.valueCanonical} />
                            </div>
                        </div>
                    </div>
                </>}
            </>
        }
        return <>
            <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: inTableCell ? "6px" : "24px" }}>
                <ArrayEntry datatype='Coding' elementName='coding' fieldLabel='Coding'
                    startingValue={Array.isArray(codeableConceptState.coding) ? codeableConceptState.coding : undefined}
                    setResourceState={setCodeableConceptState}
                    allowedValues={allowedValues} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} valueSet={valueSet}
                    systemChoices={systemChoices} systemChoicesOpen={systemChoicesOpen} />
                <StringEntry elementName='text' fieldLabel='Text' startingValue={codeableConceptState.text || ""}
                    setResourceState={setCodeableConceptState} marginTop={"6px"} />
                <UriEntry elementName='valueCanonical' setResourceState={setCodeOptionsExtensionState}
                    fieldLabel={'Add ValueSet URL for code options'}
                    startingValue={codeOptionsExtensionState.valueCanonical} />
            </div>
        </>
    }
})

export default CodeableConceptEntry;
export { DisplayFromFHIRCodeableConcept, displayStringFromFHIRCodeableConcept, simpleDisplayStringFromFHIRCodeableConcept };